import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CreditCard, TrendingUp, Zap, Mic, Image, Search, Clock, ChevronDown, ChevronUp, FileText, RefreshCw, HelpCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Popover from '../components/Popover';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import CreditsDashboard from '../components/billing/CreditsDashboard';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, CardHeader, CardTitle, Grid, Flex,
  StatCard, StatValue, StatLabel,
  Badge, Spinner, LoadingContainer
} from '../components/ui';
import { theme } from '../styles/theme';
import { formatCurrency as fmtCur, currencyLocale } from '../utils/currency';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../services/api';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const TIER_PRICES = { starter: 297, growth: 697, pro: 1497, enterprise: null };
const TIER_LABELS = { starter: 'Starter', growth: 'Growth', pro: 'Pro', enterprise: 'Enterprise', essencial: 'Essencial', trial: 'Trial' };

// usa utilitário de currency.js
const formatCurrency = (value, currency = 'BRL') => fmtCur(value, currency, currencyLocale(currency));

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function last6Months() {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(d.toISOString().slice(0, 7));
  }
  return months;
}

const PlanChangeBtn = styled.button`
  font-size: ${theme.typography.xs}; color: ${theme.colors.primary};
  background: none; border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.radii.md}; padding: 4px ${theme.spacing[3]};
  cursor: pointer; &:hover { background: ${theme.colors.primaryLight}; }
`;
const PlanSelector = styled.div`margin-top: ${theme.spacing[5]}; padding-top: ${theme.spacing[4]}; border-top: 1px solid ${theme.colors.border};`;
const PlanSelectorTitle = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[3]};`;
const PlanGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: ${theme.spacing[3]};`;
const PlanCard = styled.div`
  border: 2px solid ${p => p.$current ? theme.colors.primary : p.$upgrade ? theme.colors.success : theme.colors.border};
  border-radius: ${theme.radii.xl}; padding: ${theme.spacing[4]};
  background: ${p => p.$current ? theme.colors.primaryLight : theme.colors.white};
`;
const PlanCardName = styled.div`font-size: ${theme.typography.base}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[1]};`;
const PlanCardPrice = styled.div`font-size: ${theme.typography.xl}; font-weight: ${theme.typography.bold}; color: ${theme.colors.primary}; margin-bottom: ${theme.spacing[2]}; span { font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-weight: 400; }`;
const PlanCardLimits = styled.ul`list-style: none; padding: 0; margin: 0 0 ${theme.spacing[3]} 0; font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; display: flex; flex-direction: column; gap: 4px;`;
const PlanCurrentBadge = styled.div`text-align: center; font-size: ${theme.typography.xs}; color: ${theme.colors.primary}; font-weight: ${theme.typography.semibold};`;
const PlanSelectBtn = styled.button`
  width: 100%; padding: ${theme.spacing[2]};
  background: ${p => p.$upgrade ? theme.colors.success : theme.colors.gray100};
  color: ${p => p.$upgrade ? 'white' : theme.colors.textPrimary};
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; &:hover { opacity: 0.9; } &:disabled { opacity: 0.5; cursor: not-allowed; }
`;


export default function BillingPage({ tenantId: propTenantId }) {
  return stripePromise ? (
    <Elements stripe={stripePromise}>
      <BillingPageInner tenantId={propTenantId} />
    </Elements>
  ) : <BillingPageInner tenantId={propTenantId} />;
}

function BillingPageInner() {
  const { t, i18n } = useTranslation('billing');
  const { tenantId } = useParams();
  const toast   = useToast();
  const confirm  = useConfirm();
  const stripeHook  = useStripe();
  const elementsHook = useElements();
  const [tenant, setTenant]   = useState(null);
  const [usage, setUsage]     = useState(null);
  const [history, setHistory] = useState([]);
  const [tiers, setTiers]     = useState([]);

  const [loading, setLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [activeTab, setActiveTab] = useState('plan');
  const [invoices, setInvoices]           = useState([]);
  const [retrying, setRetrying]           = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { type, title, msg, onConfirm }
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardForm, setCardForm]         = useState({ cardName: '', cardNumber: '', cardExpiry: '', cardCvc: '', postalCode: '', addressNumber: '' });
  const [savingCard, setSavingCard]     = useState(false);
  const [pendingUpgradeTier, setPendingUpgradeTier] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { loadData(); }, [tenantId]); // eslint-disable-line

  const loadData = async () => {
    try {
      const [tenantRes, usageRes, tiersRes] = await Promise.all([
        api.get(`/tenants/${tenantId}`),
        api.get(`/usage/${tenantId}?breakdown=true`),
        api.get(`/platform/tiers?tenantId=${tenantId}`),
      ]);
      setTenant(tenantRes.data);
      setUsage(usageRes.data);
      setTiers(tiersRes.data.tiers || []);

      // Histórico dos últimos 6 meses
      const months = last6Months().filter(m => m !== currentMonth());
      const histRes = await Promise.all(
        months.map(m => api.get(`/usage/${tenantId}?month=${m}`).catch(() => ({ data: { month: m, messages: 0 } })))
      );
      setHistory(histRes.map(r => r.data));

      // Histórico de faturas
      const invRes = await api.get(`/billing/${tenantId}/invoices`).catch(() => ({ data: { invoices: [] } }));
      setInvoices(invRes.data.invoices || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const tier     = tenant?.tier || 'starter';
  const currency = tenant?.currency || 'BRL';
  const billingAnnual = tenant?.billingAnnual || false;
  const annualPaidUntil = tenant?.annualPaidUntil || null;
  const currentTierObj = tiers.find(t => t.tierId === tier);
  const hasAnnualPrice = !!(currentTierObj?.prices?.[currency]?.annual);
  const basePrice = currentTierObj?.prices?.[currency]?.monthly || TIER_PRICES[tier];
  const basePriceMonthly = basePrice;

  const formatCard   = v => v.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim().slice(0, 19);
  const formatExpiry = v => { const n = v.replace(/\D/g, ''); return n.length >= 3 ? `${n.slice(0,2)}/${n.slice(2,4)}` : n; };
  const setCard = (f, v) => setCardForm(p => ({ ...p, [f]: v }));

  const handleUpdateCard = async () => {
    const isStripe = currency !== 'BRL';
    if (!isStripe && (!cardForm.cardName || !cardForm.cardNumber || !cardForm.cardExpiry || !cardForm.cardCvc)) return;
    setSavingCard(true);
    try {
      let creditCard;
      if (isStripe) {
        if (!stripeHook || !elementsHook) { toast.error(t('stripeNotLoaded', { defaultValue: 'Stripe não carregado' })); return; }
        const { paymentMethod, error: stripeError } = await stripeHook.createPaymentMethod({
          type: 'card',
          card: elementsHook.getElement(CardElement),
          billing_details: { name: tenant?.adminName || tenant?.name, email: tenant?.billingEmail || tenant?.email }
        });
        if (stripeError) { toast.error(stripeError.message); return; }
        creditCard = { token: paymentMethod.id };
      } else {
        const [expiryMonth, expiryYear] = cardForm.cardExpiry.split('/');
        creditCard = {
          holderName:  cardForm.cardName,
          number:      cardForm.cardNumber.replace(/\s/g, ''),
          expiryMonth,
          expiryYear:  expiryYear?.length === 2 ? `20${expiryYear}` : expiryYear,
          ccv:         cardForm.cardCvc
        };
      }
      await api.post(`/billing/${tenantId}/update-card`, {
        creditCard,
        creditCardHolderInfo: {
          name:          cardForm.cardName || tenant?.adminName || tenant?.name,
          email:         tenant?.billingEmail || tenant?.email || '',
          cpfCnpj:       tenant?.document?.replace(/\D/g, '') || '',
          postalCode:    cardForm.postalCode.replace(/\D/g, ''),
          addressNumber: cardForm.addressNumber,
          phone:         tenant?.phone || ''
        }
      });
      toast.success(t('card.savedSuccess', { defaultValue: 'Cartão atualizado!' }));
      setShowCardForm(false);
      setCardForm({ cardName: '', cardNumber: '', cardExpiry: '', cardCvc: '', postalCode: '', addressNumber: '' });
      await loadData();
      // Se havia upgrade pendente, retoma automaticamente
      if (pendingUpgradeTier) {
        const tid = pendingUpgradeTier;
        setPendingUpgradeTier(null);
        setActiveTab('plan');
        setChangingPlan(true);
        setTimeout(() => handleChangePlan(tid), 300);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally { setSavingCard(false); }
  };

  const handleReactivate = async () => {
    const isStripe = currency !== 'BRL';
    setRetrying(true);
    try {
      let body = {};
      // Se o formulário de cartão estiver aberto, inclui o novo cartão na reativação
      if (showCardForm) {
        if (isStripe) {
          if (!stripeHook || !elementsHook) { toast.error(t('stripeNotLoaded', { defaultValue: 'Stripe não carregado' })); return; }
          const { paymentMethod, error: stripeError } = await stripeHook.createPaymentMethod({
            type: 'card',
            card: elementsHook.getElement(CardElement),
            billing_details: { name: tenant?.adminName || tenant?.name, email: tenant?.billingEmail || tenant?.email }
          });
          if (stripeError) { toast.error(stripeError.message); return; }
          body.creditCard = { token: paymentMethod.id };
        } else if (cardForm.cardNumber) {
          const [expiryMonth, expiryYear] = cardForm.cardExpiry.split('/');
          body.creditCard = {
            holderName:  cardForm.cardName,
            number:      cardForm.cardNumber.replace(/\s/g, ''),
            expiryMonth,
            expiryYear:  expiryYear?.length === 2 ? `20${expiryYear}` : expiryYear,
            ccv:         cardForm.cardCvc
          };
          body.creditCardHolderInfo = {
            name:          cardForm.cardName,
            email:         tenant?.billingEmail || tenant?.email || '',
            cpfCnpj:       tenant?.document?.replace(/\D/g, '') || '',
            postalCode:    cardForm.postalCode.replace(/\D/g, ''),
            addressNumber: cardForm.addressNumber,
            phone:         tenant?.phone || ''
          };
        }
      }
      const res = await api.post(`/billing/${tenantId}/reactivate`, body);
      toast.success(t('reactivate.success', { defaultValue: 'Cobrança enviada! Sua conta será reativada após confirmação do pagamento.' }));
      if (res.data.invoiceUrl) window.open(res.data.invoiceUrl, '_blank');
      setShowCardForm(false);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally { setRetrying(false); }
  };

  const handleChangePlan = async (newTierId) => {
    if (newTierId === tier) return;
    const newTierObj = tiers.find(t => t.tierId === newTierId);
    const isUpgrade = (newTierObj?.order ?? 0) > (currentTierObj?.order ?? 0);
    const newPrice = newTierObj?.prices?.[currency]?.monthly || 0;
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const daysLeft = daysInMonth - new Date().getDate() + 1;
    const proRata = isUpgrade ? ((newPrice - (basePriceMonthly || 0)) / daysInMonth * daysLeft) : 0;

    // Upgrade sem cartão — exige cadastro antes de prosseguir
    if (isUpgrade && !tenant?.creditCardToken) {
      toast.error(t('card.required', { defaultValue: 'Adicione um cartão de crédito antes de fazer upgrade.' }));
      setPendingUpgradeTier(newTierId);
      setShowCardForm(true);
      setActiveTab('payment');
      setChangingPlan(false);
      return;
    }

    const title = isUpgrade
      ? t('confirm.upgradeTitle', { name: newTierObj?.name })
      : t('confirm.downgradeTitle', { name: newTierObj?.name });
    const msg = isUpgrade
      ? t('confirm.upgradeMsg', { amount: formatCurrency(Math.max(0, proRata), currency), monthly: formatCurrency(newPrice, currency) })
      : t('confirm.downgradeMsg');

    setConfirmModal({
      title,
      msg,
      onConfirm: async () => {
        setSavingPlan(true);
        try {
          await api.post(`/billing/${tenantId}/change-plan`, { newTier: newTierId });
          setTenant(prev => ({ ...prev, tier: newTierId }));
          setChangingPlan(false);
          toast.success(t('confirm.upgradeSuccess', { defaultValue: 'Plano atualizado com sucesso!' }));
        } catch (e) {
          const errMsg = e.response?.data?.message || e.message;
          toast.error(errMsg);
          if (e.response?.status === 402) {
            setPendingUpgradeTier(newTierId);
            setShowCardForm(true);
            setActiveTab('payment');
            setChangingPlan(false);
          }
        } finally {
          setSavingPlan(false);
          setConfirmModal(null);
        }
      }
    });
  };


  const handleRetryPayment = async () => {
    setRetrying(true);
    try {
      const res = await api.post(`/billing/${tenantId}/retry-payment`);
      if (res.data.invoiceUrl) window.open(res.data.invoiceUrl, '_blank');
      await loadData();
    } catch (e) {
      console.error(e);
    } finally { setRetrying(false); }
  };



  const handleCancelAccount = async () => {
    const ok = await confirm({
      title: t('cancelAccount.title', { defaultValue: 'Cancelar conta' }),
      message: t('cancelAccount.confirm', { defaultValue: 'Tem certeza que deseja cancelar sua conta?\n\nSeu acesso permanece ativo até o fim do período pago. Após isso, seus dados serão excluídos em 30 dias.' }),
      danger: true
    });
    if (!ok) return;
    try {
      const res = await api.delete(`/tenants/${tenantId}/account`);
      toast.success(res.data.message);
      setTenant(t => ({ ...t, status: 'cancellation_pending', cancelEffectiveAt: res.data.cancelEffectiveAt }));
    } catch (e) {
      toast.error(e.response?.data?.message || t('cancelAccount.error', { defaultValue: 'Erro ao cancelar conta' }));
    }
  };

  const handleCancelNfse = (inv) => {
    setConfirmModal({
      title: t('nfseCancelTitle', { defaultValue: 'Cancelar NFS-e' }),
      message: t('nfseCancelConfirm', { defaultValue: `Confirma o cancelamento da NFS-e do período ${inv.period}?` }),
      onConfirm: async () => {
        try {
          await api.delete(`/nfse/${tenantId}/${inv.period}`);
          toast.success(t('nfseCancelSuccess', { defaultValue: 'NFS-e cancelada com sucesso' }));
          setInvoices(prev => prev.map(i => i.period === inv.period ? { ...i, nfseStatus: 'cancelled' } : i));
        } catch (e) {
          toast.error(t('nfseCancelError', { defaultValue: `Erro ao cancelar: ${e.response?.data || e.message}` }));
        }
        setConfirmModal(null);
      }
    });
  };

  const breadcrumbItems = [
    user.role === 'sallus_admin'
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : user.role === 'manager'
        ? { label: t('home'), path: `/manager/${tenantId}` }
        : { label: t('home'), path: `/dashboard/${tenantId}` },
    user.role === 'sallus_admin'
      ? { label: tenant?.name || tenantId, path: `/dashboard/${tenantId}` }
      : null,
    { label: t('title') }
  ].filter(Boolean);

  if (loading) return (
    <>
      <AppHeader title={t("title")} />
      <PageContainer>
        <LoadingContainer><Spinner /><span>{t("loading")}</span></LoadingContainer>
      </PageContainer>
    </>
  );

  return (
    <>
      <AppHeader title={t("title")} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />

        <PageHeader>
          <div>
            <PageTitle>{t("title")}</PageTitle>
            <PageSubtitle>{t("subtitle")}</PageSubtitle>
          </div>
        </PageHeader>

        {/* Banner de conta suspensa */}
        {tenant?.status === 'suspended' && (
          <SuspendedBanner>
            <Flex gap={theme.spacing[2]} style={{ alignItems: 'flex-start' }}>
              <AlertTriangle size={20} color="#92400e" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <SuspendedTitle>{t('suspended.title', { defaultValue: 'Conta suspensa por inadimplência' })}</SuspendedTitle>
                <SuspendedDesc>{t('suspended.desc', { defaultValue: 'Regularize o pagamento para reativar o acesso completo.' })}</SuspendedDesc>
              </div>
            </Flex>
            <Flex gap={theme.spacing[2]}>
              <SuspendedCardBtn onClick={() => setShowCardForm(v => !v)}>
                <CreditCard size={13} /> {showCardForm ? t('card.cancel') : t('suspended.changeCard', { defaultValue: 'Trocar cartão' })}
              </SuspendedCardBtn>
              <ReactivateBtn onClick={handleReactivate} disabled={retrying}>
                <RefreshCw size={13} /> {retrying ? t('processing') : t('suspended.reactivate', { defaultValue: 'Reativar conta' })}
              </ReactivateBtn>
            </Flex>
          </SuspendedBanner>
        )}

        <TabBar>
          {['plan','usage','payment','account'].map(tab => (
            <TabBtn key={tab} $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
              {{'plan': t('tabs.plan', { defaultValue: 'Plano' }),'usage': t('tabs.usage', { defaultValue: 'Uso' }),'payment': t('tabs.payment', { defaultValue: 'Pagamento' }),'account': t('tabs.account', { defaultValue: 'Conta' })}[tab]}
            </TabBtn>
          ))}
        </TabBar>

        {/* ── Tab: Plano ── */}
        {activeTab === 'plan' && (
          <TabPanel>
        {/* Current plan header + estimated bill */}
        <Grid min="320px" style={{ marginBottom: theme.spacing[6] }}>
          <Card>
            <CardHeader>
              <Flex gap={theme.spacing[2]}>
                <CreditCard size={18} color={theme.colors.primary} />
                <CardTitle>{t("currentPlan")}</CardTitle>
              </Flex>
              <Badge variant={tier === 'pro' ? 'primary' : tier === 'growth' ? 'success' : 'neutral'}>
                {currentTierObj?.name || TIER_LABELS[tier] || tier}
              </Badge>
            </CardHeader>
            <FaturaWrap>
              <FaturaLinha>
                <span>{t('monthly', { tier: currentTierObj?.name || TIER_LABELS[tier] || tier })}</span>
                <span>{basePrice != null ? formatCurrency(billingAnnual ? basePrice / 12 : basePrice, currency) : t('onRequest')}<small style={{ color: theme.colors.textMuted, marginLeft: 4 }}>{billingAnnual ? t('perMonthAnnual') : t('perMonth')}</small></span>
              </FaturaLinha>
              <FaturaTotal>
                <span>{t('totalEstimated')}</span>
                <span>{basePrice != null ? formatCurrency(billingAnnual ? basePrice / 12 : basePrice, currency) : t('onRequest')}</span>
              </FaturaTotal>
              <div style={{ marginTop: theme.spacing[3], paddingTop: theme.spacing[3], borderTop: `1px solid ${theme.colors.border}`, opacity: hasAnnualPrice ? 1 : 0.4, pointerEvents: hasAnnualPrice ? 'auto' : 'none' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: hasAnnualPrice ? 'pointer' : 'not-allowed', fontSize: theme.typography.sm }}>
                  <input
                    type="checkbox"
                    checked={billingAnnual}
                    disabled={!hasAnnualPrice}
                    onChange={async (e) => {
                      const annual = e.target.checked;
                      try {
                        await api.post(`/billing/${tenantId}/change-plan`, { newTier: tier, billingAnnual: annual });
                        setTenant(prev => ({ ...prev, billingAnnual: annual }));
                        toast.success(annual ? t('annualActivated', { defaultValue: 'Plano anual ativado!' }) : t('monthlyActivated', { defaultValue: 'Plano mensal ativado no próximo ciclo.' }));
                        await loadData();
                      } catch (err) {
                        toast.error(err.response?.data?.message || err.message);
                      }
                    }}
                  />
                  <span>{t('annualBilling')} <strong style={{ color: theme.colors.success }}>{t('annualDiscount')}</strong></span>
                  {!hasAnnualPrice && <span style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>{t('annualNotAvailable', { defaultValue: 'Não disponível neste plano' })}</span>}
                  {hasAnnualPrice && annualPaidUntil && <span style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>{t('annualValidUntil', { defaultValue: `Válido até ${annualPaidUntil}` })}</span>}
                </label>
              </div>
            </FaturaWrap>
          </Card>
        </Grid>

        {/* Credits Dashboard */}
        <CreditsDashboard tenantId={tenantId} />

        {/* Plan selector for upgrade/downgrade */}
        {currentTierObj && (
          <Card style={{ marginTop: theme.spacing[6] }}>
            <CardHeader>
              <Flex gap={theme.spacing[2]}>
                <Zap size={18} color={theme.colors.primary} />
                <CardTitle>{t('planResources', { name: currentTierObj.name })}</CardTitle>
              </Flex>
              <PlanChangeBtn onClick={() => setChangingPlan(v => !v)}>
                {changingPlan ? t('cancelPlan') : t('changePlan')}
              </PlanChangeBtn>
            </CardHeader>

            {/* Seletor de plano */}
            {changingPlan && (
              <PlanSelector>
                <PlanSelectorTitle>{t('selectNewPlan')}</PlanSelectorTitle>
                <PlanGrid>
                  {tiers.filter(t => t.active && t.tierId !== 'enterprise' && (t.isPublic !== false || t.tierId === tenant?.tier) && (!currentTierObj?.audience || t.audience === currentTierObj.audience)).map(tier => {
                    const price = tier.prices?.[currency]?.monthly;
                    const isCurrent = tier.tierId === tenant?.tier;
                    const isUpgrade = (tier.order ?? 0) > (currentTierObj?.order ?? 0);
                    return (
                      <PlanCard key={tier.tierId} $current={isCurrent} $upgrade={isUpgrade && !isCurrent}>
                        <PlanCardName>{tier.name}</PlanCardName>
                        <PlanCardPrice>{price != null ? formatCurrency(price, currency) : t('onRequest')}<span>{t('perMonth')}</span></PlanCardPrice>
                        <PlanCardLimits>
                          <li>{tier.monthlyCredits ? `${tier.monthlyCredits.toLocaleString()} ${t('limits.credits', { defaultValue: 'créditos/mês' })}` : tier.limits?.conversations === -1 ? '∞' : `${tier.limits?.conversations || 0} ${t('limits.conversations')}`}</li>
                          <li>{tier.limits?.agents === -1 ? '∞' : tier.limits?.agents} {t('limits.agents')}</li>
                        </PlanCardLimits>
                        {isCurrent
                          ? <PlanCurrentBadge>{t('currentPlanBadge')}</PlanCurrentBadge>
                          : <PlanSelectBtn $upgrade={isUpgrade} onClick={() => handleChangePlan(tier.tierId)} disabled={savingPlan}>
                              {savingPlan ? '...' : isUpgrade ? t('doUpgrade') : t('doDowngrade')}
                            </PlanSelectBtn>
                        }
                      </PlanCard>
                    );
                  })}
                </PlanGrid>
              </PlanSelector>
            )}
          </Card>
        )}

          </TabPanel>
        )}

        {/* ── Tab: Uso ── */}
        {activeTab === 'usage' && (
          <TabPanel>
        {/* Stats de uso */}
        <Grid min="160px" style={{ marginBottom: theme.spacing[6] }}>
          <StatCard accent={theme.colors.primary}>
            <Flex gap={theme.spacing[2]}><Zap size={14} color={theme.colors.primary} /><StatLabel>{t('messages')}</StatLabel></Flex>
            <StatValue>{(usage?.messages || 0).toLocaleString('pt-BR')}</StatValue>
          </StatCard>
          <StatCard accent="#8b5cf6">
            <Flex gap={theme.spacing[2]}><TrendingUp size={14} color="#8b5cf6" /><StatLabel>{t('tokensIn')}</StatLabel><Popover content={t('tooltips.tokensIn')}><HelpCircle size={12} color="#8b5cf6" /></Popover></Flex>
            <StatValue>{((usage?.tokensInput || 0) / 1000).toFixed(1)}K</StatValue>
          </StatCard>
          <StatCard accent="#06b6d4">
            <Flex gap={theme.spacing[2]}><TrendingUp size={14} color="#06b6d4" /><StatLabel>{t('tokensOut')}</StatLabel><Popover content={t('tooltips.tokensOut')}><HelpCircle size={12} color="#06b6d4" /></Popover></Flex>
            <StatValue>{((usage?.tokensOutput || 0) / 1000).toFixed(1)}K</StatValue>
          </StatCard>
          <StatCard accent={theme.colors.warning}>
            <Flex gap={theme.spacing[2]}><Mic size={14} color={theme.colors.warning} /><StatLabel>{t('audio')}</StatLabel></Flex>
            <StatValue>{(usage?.transcribeSeconds || 0).toLocaleString('pt-BR')}</StatValue>
          </StatCard>
          <StatCard accent={theme.colors.success}>
            <Flex gap={theme.spacing[2]}><Image size={14} color={theme.colors.success} /><StatLabel>{t('images')}</StatLabel></Flex>
            <StatValue>{(usage?.imagesProcessed || 0).toLocaleString('pt-BR')}</StatValue>
          </StatCard>
          <StatCard accent={theme.colors.danger}>
            <Flex gap={theme.spacing[2]}><Search size={14} color={theme.colors.danger} /><StatLabel>{t('kbSearches')}</StatLabel></Flex>
            <StatValue>{(usage?.kbSearches || 0).toLocaleString('pt-BR')}</StatValue>
          </StatCard>
        </Grid>
        {/* Breakdown por agente */}
        {usage?.byAgent && Object.keys(usage.byAgent).length > 0 && (
          <Card style={{ marginBottom: theme.spacing[6] }}>
            <CardHeader>
              <Flex gap={theme.spacing[2]}>
                <Clock size={18} color={theme.colors.primary} />
                <CardTitle>{t('byAgent', { month: usage.month })}</CardTitle>
              </Flex>
              <ToggleBtn onClick={() => setShowBreakdown(v => !v)}>
                {showBreakdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {showBreakdown ? t('hide') : t('expand')}
              </ToggleBtn>
            </CardHeader>
            {showBreakdown && (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('agentCol')}</Th>
                    <Th align="right">{t('messagesCol')}</Th>
                    <Th align="right">{t('tokensInCol')}</Th>
                    <Th align="right">{t('tokensOutCol')}</Th>
                    <Th align="right">{t('toolCallsCol')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usage.byAgent).map(([agentId, data]) => (
                    <tr key={agentId}>
                      <Td><AgentName>{agentId}</AgentName></Td>
                      <Td align="right">{(data.messages || 0).toLocaleString('pt-BR')}</Td>
                      <Td align="right">{((data.tokensInput || 0) / 1000).toFixed(1)}K</Td>
                      <Td align="right">{((data.tokensOutput || 0) / 1000).toFixed(1)}K</Td>
                      <Td align="right">{data.toolCalls || 0}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}

        {/* Histórico mensal */}
        {history.length > 0 && (
          <Card>
            <CardHeader>
              <Flex gap={theme.spacing[2]}>
                <TrendingUp size={18} color={theme.colors.primary} />
                <CardTitle>{t('usageHistory')}</CardTitle>
              </Flex>
            </CardHeader>
            <HistoryGrid>
              {history.map(h => {
                const [year, month] = h.month.split('-');
                return (
                  <HistoryItem key={h.month}>
                    <HistoryMonth>{new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(new Date(parseInt(year), parseInt(month) - 1))}/{year.slice(2)}</HistoryMonth>
                    <HistoryValue>{(h.messages || 0).toLocaleString('pt-BR')}</HistoryValue>
                    <HistoryLabel>{t('messagesLabel')}</HistoryLabel>
                  </HistoryItem>
                );
              })}
            </HistoryGrid>
          </Card>
        )}

          </TabPanel>
        )}

        {/* ── Tab: Pagamento ── */}
        {activeTab === 'payment' && (
          <TabPanel>
        {/* Banner de upgrade pendente */}
        {pendingUpgradeTier && (() => {
          const pt = tiers.find(t => t.tierId === pendingUpgradeTier);
          return (
            <PendingUpgradeBanner>
              <CreditCard size={16} color="#1e40af" />
              <span>{t('card.requiredForUpgrade', { plan: pt?.name || pendingUpgradeTier, defaultValue: `Cadastre um cartão para concluir o upgrade para ${pt?.name || pendingUpgradeTier}.` })}</span>
            </PendingUpgradeBanner>
          );
        })()}
        {/* Método de pagamento */}
        <Card style={{ marginBottom: theme.spacing[6] }}>
          <CardHeader>
            <Flex gap={theme.spacing[2]}>
              <CreditCard size={18} color={theme.colors.primary} />
              <CardTitle>{t('card.title')}</CardTitle>
            </Flex>
            <PlanChangeBtn onClick={() => setShowCardForm(v => !v)}>
              {showCardForm ? t('card.cancel') : tenant?.creditCardToken ? t('card.update') : t('card.add')}
            </PlanChangeBtn>
          </CardHeader>

          {!showCardForm && (
            <CardInfo>
              {tenant?.creditCardToken
                ? <><CreditCard size={14} /> {t('card.saved')}</>
                : <span style={{ color: theme.colors.textMuted }}>{t('card.none')}</span>
              }
            </CardInfo>
          )}

          {showCardForm && (
            <CardFormWrap>
              {currency !== 'BRL' ? (
                // Stripe: usa CardElement (tokeniza no browser)
                <>
                  <CardFormGroup>
                    <CardFormLabel>{t('card.details', { defaultValue: 'Dados do cartão' })}</CardFormLabel>
                    <StripeCardWrapper>
                      <CardElement options={{ style: { base: { fontSize: '14px', color: theme.colors.textPrimary, '::placeholder': { color: theme.colors.gray400 } } } }} />
                    </StripeCardWrapper>
                  </CardFormGroup>
                  <SaveCardBtn onClick={handleUpdateCard} disabled={savingCard}>
                    <CreditCard size={14} /> {savingCard ? t('card.saving') : t('card.save')}
                  </SaveCardBtn>
                </>
              ) : (
                // Asaas: campos manuais
                <>
                  <CardFormRow>
                    <CardFormGroup>
                      <CardFormLabel>{t('card.holderName')}</CardFormLabel>
                      <CardFormInput value={cardForm.cardName} onChange={e => setCard('cardName', e.target.value.toUpperCase())} placeholder={t('card.holderNamePlaceholder')} />
                    </CardFormGroup>
                  </CardFormRow>
                  <CardFormRow>
                    <CardFormGroup style={{ flex: 2 }}>
                      <CardFormLabel>{t('card.number')}</CardFormLabel>
                      <CardFormInput value={cardForm.cardNumber} onChange={e => setCard('cardNumber', formatCard(e.target.value))} placeholder="0000 0000 0000 0000" maxLength={19} />
                    </CardFormGroup>
                    <CardFormGroup>
                      <CardFormLabel>{t('card.expiry')}</CardFormLabel>
                      <CardFormInput value={cardForm.cardExpiry} onChange={e => setCard('cardExpiry', formatExpiry(e.target.value))} placeholder="MM/AA" maxLength={5} />
                    </CardFormGroup>
                    <CardFormGroup>
                      <CardFormLabel>{t('card.cvv')}</CardFormLabel>
                      <CardFormInput value={cardForm.cardCvc} onChange={e => setCard('cardCvc', e.target.value.replace(/\D/g,'').slice(0,4))} placeholder="000" maxLength={4} />
                    </CardFormGroup>
                  </CardFormRow>
                  <CardFormRow>
                    <CardFormGroup>
                      <CardFormLabel>{t('card.postalCode')}</CardFormLabel>
                      <CardFormInput value={cardForm.postalCode} onChange={e => setCard('postalCode', e.target.value.replace(/\D/g,'').slice(0,8).replace(/(\d{5})(\d)/,'$1-$2'))} placeholder="00000-000" maxLength={9} />
                    </CardFormGroup>
                    <CardFormGroup>
                      <CardFormLabel>{t('card.addressNumber')}</CardFormLabel>
                      <CardFormInput value={cardForm.addressNumber} onChange={e => setCard('addressNumber', e.target.value)} placeholder="123" />
                    </CardFormGroup>
                  </CardFormRow>
                  <SaveCardBtn onClick={handleUpdateCard} disabled={savingCard || !cardForm.cardName || !cardForm.cardNumber || !cardForm.cardExpiry || !cardForm.cardCvc}>
                    <CreditCard size={14} /> {savingCard ? t('card.saving') : t('card.save')}
                  </SaveCardBtn>
                </>
              )}
            </CardFormWrap>
          )}
        </Card>

        {/* Faturas */}
        <Card style={{ marginBottom: theme.spacing[6] }}>
          <CardHeader>
            <Flex gap={theme.spacing[2]}>
              <FileText size={18} color={theme.colors.primary} />
              <CardTitle>{t('invoiceHistory')}</CardTitle>
            </Flex>
          </CardHeader>
          {invoices.length === 0 ? (
            <EmptyInvoices>{t('noInvoices', { defaultValue: 'Nenhuma fatura gerada ainda.' })}</EmptyInvoices>
          ) : (
            <InvoiceTable>
              <thead>
                <tr>
                  <Th>{t('periodCol', { defaultValue: 'Período' })}</Th>
                  <Th>{t('planCol', { defaultValue: 'Plano' })}</Th>
                  <Th align="right">{t('amountCol', { defaultValue: 'Valor' })}</Th>
                  <Th align="center">{t('statusCol', { defaultValue: 'Status' })}</Th>
                  <Th align="center">{t('dueDateCol', { defaultValue: 'Vencimento' })}</Th>
                  <Th align="center">{t('nfseCol')}</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.period}>
                    <Td>{inv.period.startsWith('extra-') ? t('invoicePeriod.extraPack', { defaultValue: 'Pacote extra' }) : inv.period}</Td>
                    <Td style={{ textTransform: 'capitalize' }}>{inv.billingType === 'extra_pack' ? (inv.description || t('invoiceTier.extraPack', { defaultValue: 'Créditos extras' })) : inv.tier}</Td>
                    <Td align="right">{formatCurrency(inv.totalAmount, inv.currency)}</Td>
                    <Td align="center">
                      <InvoiceStatusBadge $status={inv.status}>
                        {inv.status === 'paid'              ? t('invoiceStatus.paid')
                          : inv.status === 'pending'           ? t('invoiceStatus.pending')
                          : inv.status === 'preview'           ? t('invoiceStatus.preview')
                          : inv.status?.startsWith('failed')   ? t('invoiceStatus.failed')
                          : inv.status === 'cancelled'         ? t('invoiceStatus.cancelled')
                          : inv.status}
                      </InvoiceStatusBadge>
                    </Td>
                    <Td align="center">{inv.dueDate || '—'}</Td>
                    <Td align="center">
                      {inv.currency === 'BRL' && inv.status === 'paid' ? (
                        inv.nfseUrl
                          ? <InvoiceLink href={inv.nfseUrl} target="_blank" rel="noopener noreferrer">{t('nfseView')}</InvoiceLink>
                          : <NfseStatusText $status={inv.nfseStatus}>
                              {inv.nfseStatus ? t(`nfseStatus.${inv.nfseStatus}`, { defaultValue: inv.nfseStatus }) : t('nfseStatus.processing')}
                            </NfseStatusText>
                      ) : <span style={{ color: theme.colors.textMuted }}>{t('nfseNotApplicable')}</span>}
                      {user.role === 'sallus_admin' && inv.nfseStatus === 'issued' && (
                        <CancelNfseBtn onClick={() => handleCancelNfse(inv)}>
                          {t('nfseCancelBtn', { defaultValue: 'Cancelar NF' })}
                        </CancelNfseBtn>
                      )}
                    </Td>
                    <Td align="center">
                      {(inv.invoiceUrl || inv.bankSlipUrl) && (
                        <InvoiceLink href={inv.invoiceUrl || inv.bankSlipUrl} target="_blank" rel="noopener noreferrer">
                          t('viewInvoice')
                        </InvoiceLink>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </InvoiceTable>
          )}
        </Card>

          </TabPanel>
        )}

        {/* ── Tab: Conta ── */}
        {activeTab === 'account' && (
          <TabPanel>
        {/* Zona de perigo */}
        {tenant?.status !== 'cancelled' && (
          <DangerZone>
            <DangerZoneTitle>{t('dangerZone.title', { defaultValue: 'Zona de Perigo' })}</DangerZoneTitle>
            {tenant?.status === 'cancellation_pending' ? (
              <DangerZoneMsg>
                {t('dangerZone.pendingPrefix', { defaultValue: '⚠️ Cancelamento solicitado. Seu acesso permanece ativo até' })}{' '}
                <strong>{tenant.cancelEffectiveAt ? new Date(tenant.cancelEffectiveAt).toLocaleDateString('pt-BR') : t('dangerZone.endOfPeriod', { defaultValue: 'o fim do período atual' })}</strong>.
                {' '}{t('dangerZone.pendingSuffix', { defaultValue: 'Seus dados serão excluídos 30 dias após essa data.' })}
              </DangerZoneMsg>
            ) : (
              <>
                <DangerZoneMsg>
                  {t('dangerZone.desc', { defaultValue: 'Ao cancelar sua conta, seu acesso será mantido até o fim do período já pago. Após isso, seus dados serão excluídos em 30 dias conforme a LGPD.' })}
                </DangerZoneMsg>
                <CancelAccountBtn onClick={handleCancelAccount}>
                  {t('dangerZone.cancelBtn', { defaultValue: 'Cancelar minha conta' })}
                </CancelAccountBtn>
              </>
            )}
          </DangerZone>
        )}
          </TabPanel>
        )}
      </PageContainer>

      {/* Modal de confirmação de cobrança */}
      {confirmModal && (
        <ConfirmOverlay onClick={() => setConfirmModal(null)}>
          <ConfirmBox onClick={e => e.stopPropagation()}>
            <ConfirmTitle>{confirmModal.title}</ConfirmTitle>
            <ConfirmMsg>{confirmModal.msg}</ConfirmMsg>
            <ConfirmActions>
              <ConfirmCancelBtn onClick={() => setConfirmModal(null)}>
                {t('confirm.cancel')}
              </ConfirmCancelBtn>
              <ConfirmOkBtn onClick={confirmModal.onConfirm} disabled={savingPlan}>
                {savingPlan ? '...' : t('confirm.confirm')}
              </ConfirmOkBtn>
            </ConfirmActions>
          </ConfirmBox>
        </ConfirmOverlay>
      )}
    </>
  );
}

// ── Styled ────────────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 1px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing[6]};
`;

const TabBtn = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  font-size: ${theme.typography.sm};
  font-weight: ${p => p.$active ? theme.typography.semibold : theme.typography.normal};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textMuted};
  background: none;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  margin-bottom: -1px;
  &:hover { color: ${theme.colors.primary}; }
`;

const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FaturaWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
`;

const FaturaLinha = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${theme.typography.sm};
  color: ${p => p.$warn ? theme.colors.warning : theme.colors.textSecondary};
`;

const FaturaTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  border-top: 1px solid ${theme.colors.border};
  padding-top: ${theme.spacing[2]};
  margin-top: ${theme.spacing[1]};
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  background: none;
  border: none;
  cursor: pointer;
  &:hover { color: ${theme.colors.textPrimary}; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${theme.spacing[3]};
`;

const Th = styled.th`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textMuted};
  text-align: ${p => p.align || 'left'};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.border};
`;

const Td = styled.td`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  text-align: ${p => p.align || 'left'};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.gray100};
`;

const AgentName = styled.span`
  font-weight: ${theme.typography.medium};
  text-transform: capitalize;
`;

const HistoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[3]};
`;

const HistoryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border-radius: ${theme.radii.lg};
  text-align: center;
`;

const HistoryMonth = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.medium};
`;

const HistoryValue = styled.div`
  font-size: ${theme.typography.xl};
  font-weight: ${theme.typography.bold};
  color: ${theme.colors.textPrimary};
`;

const HistoryLabel = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;





const SuspendedBanner = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: ${theme.colors.warningLight}; border: 1px solid #fcd34d;
  border-radius: ${theme.radii.xl}; margin-bottom: ${theme.spacing[6]};
`;
const SuspendedTitle = styled.div`
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: #92400e;
`;
const SuspendedDesc = styled.div`
  font-size: ${theme.typography.xs}; color: #b45309; margin-top: 2px;
`;
const SuspendedCardBtn = styled.button`
  display: flex; align-items: center; gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: white; color: #92400e;
  border: 1px solid #fcd34d; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; &:hover { background: ${theme.colors.warningLight}; }
`;
const ReactivateBtn = styled.button`
  display: flex; align-items: center; gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: #d97706; color: white;
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  cursor: pointer; &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #b45309; }
`;
const StripeCardWrapper = styled.div`
  padding: ${theme.spacing[3]}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; background: white;
  &:focus-within { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;

const InvoiceTable = styled.table`
  width: 100%; border-collapse: collapse; margin-top: ${theme.spacing[3]};
`;

const InvoiceStatusBadge = styled.span`
  font-size: ${theme.typography.xs};
  color: ${p => p.$status === 'paid' ? theme.colors.success
    : p.$status === 'pending' ? '#92400e'
    : p.$status?.startsWith('failed') ? theme.colors.danger
    : theme.colors.textMuted};
`;

const InvoiceLink = styled.a`
  font-size: ${theme.typography.xs}; color: ${theme.colors.primary};
  text-decoration: none; &:hover { text-decoration: underline; }
`;

const EmptyInvoices = styled.div`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted};
  padding: ${theme.spacing[4]}; text-align: center;
`;

const NfseStatusText = styled.span`
  font-size: ${theme.typography.xs};
  color: ${p => p.$status === 'issued' ? theme.colors.success
    : p.$status === 'error' ? theme.colors.danger
    : theme.colors.textMuted};
`;

const CancelNfseBtn = styled.button`
  display: block; margin-top: 4px;
  font-size: ${theme.typography.xs}; color: ${theme.colors.danger};
  background: none; border: none; cursor: pointer; padding: 0;
  text-decoration: underline;
  &:hover { opacity: 0.7; }
`;

const CardInfo = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};
  padding: ${theme.spacing[2]} 0;
`;
const CardFormWrap = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]}; margin-top: ${theme.spacing[3]}; padding-top: ${theme.spacing[3]}; border-top: 1px solid ${theme.colors.border};`;
const CardFormRow = styled.div`display: flex; gap: ${theme.spacing[3]};`;
const CardFormGroup = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[1]}; flex: 1;`;
const CardFormLabel = styled.label`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const CardFormInput = styled.input`padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; outline: none; &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 2px ${theme.colors.primaryLight}; }`;
const SaveCardBtn = styled.button`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[2]} ${theme.spacing[4]}; background: ${theme.colors.primary}; color: white; border: none; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; cursor: pointer; align-self: flex-start; &:disabled { opacity: 0.5; cursor: not-allowed; } &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }`;

const ConfirmOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: ${theme.spacing[4]};
`;
const ConfirmBox = styled.div`
  background: ${theme.colors.white}; border-radius: ${theme.radii.xl};
  padding: ${theme.spacing[6]}; max-width: 440px; width: 100%;
  box-shadow: ${theme.shadows.xl};
`;
const ConfirmTitle = styled.h3`
  font-size: ${theme.typography.lg}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[3]} 0;
`;
const ConfirmMsg = styled.p`
  font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};
  line-height: 1.6; margin: 0 0 ${theme.spacing[5]} 0;
`;
const ConfirmActions = styled.div`
  display: flex; justify-content: flex-end; gap: ${theme.spacing[3]};
`;
const ConfirmCancelBtn = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[5]};
  background: none; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary}; cursor: pointer;
  &:hover { background: ${theme.colors.gray50}; }
`;
const ConfirmOkBtn = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[5]};
  background: ${theme.colors.primary}; color: white;
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
`;


const PendingUpgradeBanner = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: #eff6ff; border: 1px solid #bfdbfe;
  border-radius: ${theme.radii.lg}; margin-bottom: ${theme.spacing[4]};
  font-size: ${theme.typography.sm}; color: #1e40af;
  font-weight: ${theme.typography.medium};
`;

const DangerZone = styled.div`
  margin-top: ${theme.spacing[8]};
  border: 1.5px solid ${theme.colors.danger};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing[5]};
  background: #fff5f5;
`;
const DangerZoneTitle = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.danger};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: ${theme.spacing[3]};
`;
const DangerZoneMsg = styled.p`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing[4]};
  line-height: 1.6;
`;
const CancelAccountBtn = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: white;
  border: 1.5px solid ${theme.colors.danger};
  border-radius: ${theme.radii.md};
  color: ${theme.colors.danger};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.danger}; color: white; }
`;
